import { useState, useEffect, useCallback } from "react";
import { propertySupportService } from "../model/propertyServices";
import type {
  SexOffendersResponse,
  CrimeIncidentsResponse,
  SexOffendersOrCrimeIncidentsPayload,
  PropertyRealStateDetailInfo,
  AddressData,
  CommunitySafetyMapMarker,
  Step1ActiveTab,
} from "../model/propertiesTypes";

interface UseCommunitySafetyDataProps {
  propertyDetails?: PropertyRealStateDetailInfo;
  addressData?: AddressData;
  activeTab: Step1ActiveTab;
  distance?: number; // in meters, default 1000m (1km)
}

export const useCommunitySafetyData = ({
  propertyDetails,
  addressData,
  activeTab,
  distance = 10000,
}: UseCommunitySafetyDataProps) => {
  const [sexOffendersData, setSexOffendersData] = useState<SexOffendersResponse | null>(null);
  const [crimeIncidentsData, setCrimeIncidentsData] = useState<CrimeIncidentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get coordinates from property details
  const getCoordinates = useCallback(() => {
    // Try property details first
    if (propertyDetails?.propertyInfo?.latitude && propertyDetails?.propertyInfo?.longitude) {
      return {
        lat: Number(propertyDetails.propertyInfo.latitude),
        lon: Number(propertyDetails.propertyInfo.longitude),
      };
    }

    return null;
  }, [propertyDetails]);

  // Get city and zip from property details or address
  const getLocationInfo = useCallback(() => {
    const city = propertyDetails?.propertyInfo?.address?.city || addressData?.address?.city || "";
    const zip = propertyDetails?.propertyInfo?.address?.zip || addressData?.address?.zipCode || "";

    return { city, zip };
  }, [propertyDetails, addressData]);

  useEffect(() => {
    const coordinates = getCoordinates();
    const { city, zip } = getLocationInfo();

    if (!coordinates) {
      setError("Location coordinates not available");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate date range (last 12 months)
        const now = new Date();
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(now.getFullYear() - 1);

        const payload: Partial<SexOffendersOrCrimeIncidentsPayload> = {
          lat: coordinates.lat,
          lon: coordinates.lon,
          distance: distance / 1000, // Convert meters to kilometers
          cityname: city,
          datetime_ini: oneYearAgo.toISOString(),
          datetime_end: now.toISOString(),
          page: 1,
          zipcode: zip,
        };

        if (activeTab === "offenders") {
          // Fetch sex offenders data
          try {
            const data = await propertySupportService.getSexOffendersByLocation(payload);
            setSexOffendersData(data);
          } catch (err) {
            // Fallback to zip-based search if location-based fails
            if (zip) {
              try {
                const zipData = await propertySupportService.getSexOffendersByZip({
                  ...payload,
                  zipcode: zip,
                });
                setSexOffendersData(zipData);
              } catch (zipErr) {
                throw err; // Throw original error
              }
            } else {
              throw err;
            }
          }
        } else {
          // Fetch crime incidents data
          try {
            const data = await propertySupportService.getCrimeIncidentsByLocation(payload);
            setCrimeIncidentsData(data);
          } catch (err) {
            // Fallback to regular crime incidents endpoint
            try {
              const fallbackData = await propertySupportService.getCrimeIncidents(payload);
              setCrimeIncidentsData(fallbackData);
            } catch (fallbackErr) {
              throw err; // Throw original error
            }
          }
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch community safety data";
        setError(message);
        console.error("Error fetching community safety data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getCoordinates, getLocationInfo, activeTab, distance]);

  // Prepare markers for map
  const getMapMarkers = () => {
    const markers: CommunitySafetyMapMarker[] = [];

    if (activeTab === "offenders" && sexOffendersData?.sex_offenders) {
      sexOffendersData.sex_offenders.forEach((offender) => {
        const lat = offender.sex_offender_lat;
        const lng = offender.sex_offender_lon;

        // Check if lat/lng exist and are not empty strings
        if (lat != null && lng != null) {
          const latNum = typeof lat === "string" ? Number(lat) : lat;
          const lngNum = typeof lng === "string" ? Number(lng) : lng;

          // Validate that conversion was successful
          if (!isNaN(latNum) && !isNaN(lngNum)) {
            markers.push({
              lat: latNum,
              lng: lngNum,
              color: "#eab308", // Yellow for sex offenders (as per user's earlier request)
              type: "offender",
              data: offender, // Pass full offender data for tooltip
            });
          }
        }
      });
    } else if (activeTab === "crime" && crimeIncidentsData?.incidents) {
      crimeIncidentsData.incidents.forEach((incident) => {
        const lat = incident.incident_latitude;
        const lng = incident.incident_longitude;

        // Allow both string and numeric lat/lng, similar to sex offenders block above
        if (lat != null && lng != null) {
          const latNum = typeof lat === "string" ? Number(lat) : lat;
          const lngNum = typeof lng === "string" ? Number(lng) : lng;

          // Validate that conversion was successful
          if (!isNaN(latNum) && !isNaN(lngNum)) {
            markers.push({
              lat: latNum,
              lng: lngNum,
              color: "#ef4444", // Red for crime (as per user's earlier request)
              type: "crime",
              data: incident, // Pass full incident data for tooltip
            });
          }
        }
      });
    }

    // Debug: see what is being sent to the map
    if (markers.length) {
      console.log("[CommunitySafety] map markers", activeTab, markers.length, markers[0]);
    } else {
      console.log("[CommunitySafety] no markers for tab", activeTab, {
        activeTab,
        hasSexOffendersData: !!sexOffendersData,
        hasCrimeData: !!crimeIncidentsData,
        sexOffendersCount: sexOffendersData?.sex_offenders?.length || 0,
        crimeIncidentsCount: crimeIncidentsData?.incidents?.length || 0,
        crimeIncidentsData: crimeIncidentsData,
        firstCrimeIncident: crimeIncidentsData?.incidents?.[0],
      });
    }

    return markers;
  };

  return {
    sexOffendersData,
    crimeIncidentsData,
    loading,
    error,
    mapMarkers: getMapMarkers(),
  };
};
